import type { KundaliResponse } from "@/lib/api/kundali";

const REQUIRED_GRAHAS = new Set([
  "sun",
  "moon",
  "mars",
  "mercury",
  "jupiter",
  "venus",
  "saturn",
  "rahu",
  "ketu",
]);

const INTERPRETATION_FALLBACK_MARKERS = [
  "Your kundali calculation is ready.",
  "Aapki kundali ganana taiyar hai.",
] as const;

export type KundaliInterpretationQuality = "personalized" | "engine_fallback" | "empty";

export interface KundaliResultQuality {
  complete: boolean;
  reasons: string[];
  interpretation: KundaliInterpretationQuality;
}

export function inspectKundaliResult(result: KundaliResponse): KundaliResultQuality {
  const reasons = [
    ...identityReasons(result),
    ...chartStructureReasons(result),
    ...periodAndDoshaReasons(result),
  ];
  return {
    complete: reasons.length === 0,
    reasons,
    interpretation: interpretationQuality(result.interpretation),
  };
}

function identityReasons(result: KundaliResponse): string[] {
  const fields = {
    reading_id: result.id,
    lagna_rashi: result.lagna_rashi,
    moon_rashi: result.moon_rashi,
    sun_rashi: result.sun_rashi,
    moon_nakshatra: result.moon_nakshatra,
  };
  return Object.entries(fields)
    .filter(([, value]) => !value?.trim())
    .map(([field]) => `missing_${field}`);
}

function chartStructureReasons(result: KundaliResponse): string[] {
  const grahas = new Set((result.planets ?? []).map((planet) => planet.planet_id));
  const completeNavagraha = [...REQUIRED_GRAHAS].every((graha) => grahas.has(graha));
  const completeHouses =
    new Set((result.houses ?? []).map((house) => house.house_number)).size === 12;
  return [
    completeNavagraha ? null : "incomplete_navagraha",
    completeHouses ? null : "incomplete_twelve_houses",
  ].filter((reason): reason is string => reason !== null);
}

function periodAndDoshaReasons(result: KundaliResponse): string[] {
  const hasActiveDasha = Boolean(
    result.dasha?.current_mahadasha && result.dasha.current_antardasha
  );
  return [
    hasActiveDasha ? null : "missing_active_dasha",
    Array.isArray(result.doshas) ? null : "missing_dosha_analysis",
  ].filter((reason): reason is string => reason !== null);
}

function interpretationQuality(value: string | null | undefined): KundaliInterpretationQuality {
  const normalized = value?.trim() ?? "";
  if (!normalized) return "empty";
  return INTERPRETATION_FALLBACK_MARKERS.some((marker) => normalized.startsWith(marker))
    ? "engine_fallback"
    : "personalized";
}
